import React, { useState, useEffect } from 'react';
import { RotateCcw, Play, CheckCircle } from 'lucide-react';

const SDMTGame = () => {
  // Componentes para los símbolos oficiales del SDMT TEST
  const SymbolComponents = {
    0: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M8 4 L8 20 M8 12 L20 12" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    ),
    1: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M16 6 Q8 6 8 12 Q8 18 16 18" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    ),
    2: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <line x1="6" y1="15" x2="18" y2="15" stroke="black" strokeWidth="3"/>
          <circle cx="12" cy="9" r="2" fill="black"/>
        </svg>
      </div>
    ),
    3: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M16 4 L16 20 M4 12 L16 12" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    ),
    4: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <line x1="6" y1="9" x2="18" y2="9" stroke="black" strokeWidth="3"/>
          <circle cx="12" cy="15" r="2" fill="black"/>
        </svg>
      </div>
    ),
    5: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M8 20 L8 6 L18 6" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    ),
    6: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M8 6 Q16 6 16 12 Q16 18 8 18" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    ),
    7: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M12 6 L12 18 M6 12 L18 12" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    ),
    8: () => (
      <div className="w-8 h-8 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24">
          <path d="M8 6 L16 12 L8 18" stroke="black" strokeWidth="3" fill="none"/>
        </svg>
      </div>
    )
  };

  const [symbolNumberMap, setSymbolNumberMap] = useState({});
  const [testGrid, setTestGrid] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [timer, setTimer] = useState(0);
  const [timeLimit, setTimeLimit] = useState(90);
  const [timeLimitInput, setTimeLimitInput] = useState('90');
  const [isRunning, setIsRunning] = useState(false);
  const [score, setScore] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [timeUp, setTimeUp] = useState(false);

  const generateSymbolMap = () => {
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    const shuffledNumbers = [...numbers].sort(() => Math.random() - 0.5);
    const newMap = {};
    
    Object.keys(SymbolComponents).forEach((symbolIndex, index) => {
      newMap[symbolIndex] = shuffledNumbers[index];
    });
    
    setSymbolNumberMap(newMap);
  };

  const generateTestGrid = () => {
    const grid = [];
    const symbolIndices = Object.keys(SymbolComponents);
    for (let i = 0; i < 25; i++) {
      const randomSymbolIndex = symbolIndices[Math.floor(Math.random() * symbolIndices.length)];
      grid.push(randomSymbolIndex);
    }
    setTestGrid(grid);
  };

  const initializeGame = () => {
    generateSymbolMap();
    generateTestGrid();
    setUserAnswers({});
    setCurrentIndex(0);
    setGameStarted(false);
    setGameCompleted(false);
    setTimer(0);
    setIsRunning(false);
    setTimeUp(false);
    setScore({ correct: 0, incorrect: 0, total: 0 });
  };

  useEffect(() => {
    initializeGame();
  }, []);

  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(timer => {
          const newTimer = timer + 1;
          if (newTimer >= timeLimit) {
            setIsRunning(false);
            setTimeUp(true);
            setUserAnswers(currentAnswers => {
              completeGame(currentAnswers, true);
              return currentAnswers;
            });
          }
          return newTimer;
        });
      }, 1000);
    } else if (!isRunning) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRunning, timeLimit]);

  const handleTimeLimitChange = (e) => {
    const value = e.target.value;
    setTimeLimitInput(value);
  };

  const startGame = () => {
    const numValue = parseInt(timeLimitInput);
    let finalTimeLimit;
    
    if (!isNaN(numValue) && numValue >= 10 && numValue <= 300) {
      finalTimeLimit = numValue;
    } else {
      finalTimeLimit = 90;
      setTimeLimitInput('90');
    }
    
    setTimeLimit(finalTimeLimit);
    
    setTimeout(() => {
      setGameStarted(true);
      setIsRunning(true);
    }, 10);
  };

  const handleNumberInput = (index, value) => {
    if (!gameStarted || gameCompleted || timeUp) return;
    
    if (value === '') {
      const newAnswers = { ...userAnswers };
      delete newAnswers[index];
      setUserAnswers(newAnswers);
      return;
    }
    
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 9) {
      const newAnswers = { ...userAnswers };
      newAnswers[index] = numValue;
      setUserAnswers(newAnswers);
      
      if (Object.keys(newAnswers).length === testGrid.length) {
        completeGame(newAnswers, false);
      }
    }
  };

  const completeGame = (answers, isTimeUp = false) => {
    setIsRunning(false);
    setGameCompleted(true);
    if (isTimeUp) setTimeUp(true);
    
    let correct = 0;
    let incorrect = 0;
    let total = 0;
    
    testGrid.forEach((symbolIndex, index) => {
      if (answers[index] !== undefined) {
        total++;
        if (answers[index] === symbolNumberMap[symbolIndex]) {
          correct++;
        } else {
          incorrect++;
        }
      }
    });
    
    setScore({ correct, incorrect, total });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px', backgroundColor: 'white' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
          Test SDMT - Symbol Digit Modalities
        </h1>
        <p style={{ color: '#4b5563' }}>
          Memoriza las asociaciones símbolo-número y completa la tabla lo más rápido posible
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          onClick={initializeGame}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
        >
          <RotateCcw size={16} />
          Nuevo Juego
        </button>
        
        {!gameStarted && !gameCompleted && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="timeLimit" style={{ fontSize: '14px', fontWeight: '500' }}>
                Tiempo límite (seg):
              </label>
              <input
                id="timeLimit"
                type="number"
                min="10"
                max="300"
                value={timeLimitInput}
                onChange={handleTimeLimitChange}
                style={{ width: '64px', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center' }}
              />
            </div>
            <button
              onClick={startGame}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#10b981', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
            >
              <Play size={16} />
              Comenzar
            </button>
          </>
        )}
        
        <div style={{ fontSize: '18px', fontFamily: 'monospace', color: timer >= timeLimit - 10 && isRunning ? '#dc2626' : '#000', fontWeight: timer >= timeLimit - 10 && isRunning ? 'bold' : 'normal' }}>
          Tiempo: {formatTime(timer)} / {formatTime(timeLimit)}
        </div>
        
        {gameCompleted && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2563eb', fontWeight: 'bold' }}>
            <CheckCircle size={20} />
            {timeUp ? 'Tiempo agotado!' : 'Completado!'}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '32px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', textAlign: 'center' }}>Tabla de Referencia</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: '8px', maxWidth: '1000px', margin: '0 auto' }}>
          {Object.keys(SymbolComponents).map((symbolIndex) => {
            const SymbolComponent = SymbolComponents[symbolIndex];
            return (
              <div key={symbolIndex} style={{ textAlign: 'center' }}>
                <div style={{ height: '64px', marginBottom: '8px', padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SymbolComponent />
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2563eb' }}>
                  {symbolNumberMap[symbolIndex]}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ backgroundColor: '#f9fafb', padding: '24px', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', textAlign: 'center' }}>Completa los números</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', maxWidth: '500px', margin: '0 auto' }}>
          {testGrid.map((symbolIndex, index) => {
            const SymbolComponent = SymbolComponents[symbolIndex];
            const isCorrect = gameCompleted && userAnswers[index] === symbolNumberMap[symbolIndex];
            const isIncorrect = gameCompleted && userAnswers[index] !== undefined && userAnswers[index] !== symbolNumberMap[symbolIndex];
            
            return (
              <div key={index} style={{ textAlign: 'center' }}>
                <div style={{ height: '64px', marginBottom: '8px', padding: '12px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SymbolComponent />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[1-9]"
                  maxLength="1"
                  value={userAnswers[index] || ''}
                  onChange={(e) => handleNumberInput(index, e.target.value)}
                  style={{
                    width: '48px',
                    height: '48px',
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    border: isCorrect ? '2px solid #10b981' : isIncorrect ? '2px solid #ef4444' : '2px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: isCorrect ? '#d1fae5' : isIncorrect ? '#fee2e2' : (!gameStarted || gameCompleted || timeUp ? '#f3f4f6' : 'white'),
                    color: isCorrect ? '#065f46' : isIncorrect ? '#991b1b' : '#000',
                    cursor: (!gameStarted || gameCompleted || timeUp) ? 'not-allowed' : 'text'
                  }}
                  disabled={!gameStarted || gameCompleted || timeUp}
                />
                {gameCompleted && (
                  <div style={{ fontSize: '12px', marginTop: '4px', color: '#4b5563' }}>
                    {symbolNumberMap[symbolIndex]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {gameCompleted && (
        <div style={{ marginTop: '24px', padding: '24px', backgroundColor: '#dbeafe', borderRadius: '8px', border: '2px solid #93c5fd' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', textAlign: 'center', color: '#1e40af' }}>
            📊 Resumen de Resultados
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', textAlign: 'center' }}>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>{score.correct}</div>
              <div style={{ fontSize: '14px', color: '#4b5563' }}>Aciertos</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>{score.incorrect}</div>
              <div style={{ fontSize: '14px', color: '#4b5563' }}>Errores</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>{score.total}</div>
              <div style={{ fontSize: '14px', color: '#4b5563' }}>Completadas</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8b5cf6' }}>
                {score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#4b5563' }}>Precisión</div>
            </div>
          </div>
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '18px' }}>
              <span style={{ fontWeight: 'bold' }}>Tiempo utilizado:</span> {formatTime(timer)} / {formatTime(timeLimit)}
            </div>
            {timeUp && (
              <div style={{ color: '#dc2626', fontWeight: 'bold', marginTop: '8px' }}>
                ⏰ Se agotó el tiempo límite
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#dbeafe', borderRadius: '8px', fontSize: '14px', color: '#1f2937' }}>
        <h3 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Instrucciones:</h3>
        <ol style={{ marginLeft: '20px' }}>
          <li style={{ marginBottom: '4px' }}>Estudia la tabla de referencia que muestra qué número corresponde a cada símbolo</li>
          <li style={{ marginBottom: '4px' }}>Haz clic en "Comenzar" para iniciar el cronómetro</li>
          <li style={{ marginBottom: '4px' }}>Introduce el número correcto para cada símbolo en el grid de abajo</li>
          <li style={{ marginBottom: '4px' }}>Intenta ser lo más rápido y preciso posible</li>
          <li>El juego se completa automáticamente cuando llenes todos los campos</li>
        </ol>
      </div>
    </div>
  );
};

export default SDMTGame;